import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listTemplates, getTemplate, instantiateTemplate, ApiError } from '../api';
import { Select } from './Select';
import { templateOptions, renderTemplateOption } from './pickerOptions';

/**
 * Making a task from a template, from the day view.
 *
 * The Templates screen could already do this, but planning a day and leaving the day to
 * do it are different activities — the whole point of a template is that using one
 * should be quicker than typing the task out. The new task lands undated, which puts it
 * straight into the shortlist beside everything else waiting to be dragged onto the day.
 *
 * Templates arrive most-recently-used first (the server orders them), so the one you
 * reach for is usually near the top — which is why the picker leaves the order alone.
 *
 * The chooser was the last native `<select>` in the app after Phase 77 took the other five. It is
 * also the one with the most to say that an `<option>` cannot hold: whether a template makes one
 * task or a bundle of them, whether it recurs, what it estimates, and how often it has been used.
 * All of that was collapsed into a name.
 */
export function TemplateQuickAdd() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState('');
  const [added, setAdded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({ queryKey: ['templates'], queryFn: listTemplates });

  // Only fetched once a template is chosen, and only used to offer its variants —
  // "Chest workout" versus "Legs" is a choice worth keeping one click away.
  const detail = useQuery({
    queryKey: ['template', selected],
    queryFn: () => getTemplate(selected),
    enabled: selected !== '',
  });

  const add = useMutation({
    mutationFn: (variant?: string) => instantiateTemplate(selected, variant ? { variant } : {}),
    onSuccess: (result) => {
      setError(null);
      setAdded(result.tasks[0]?.title ?? 'Added');
      // The day plan holds the shortlist, so it has to be re-read for the new task to
      // appear there.
      void queryClient.invalidateQueries();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create that task.'),
  });

  if (templates.data && templates.data.length === 0) return null;

  const variants = detail.data?.variants ?? [];

  return (
    <div className="template-quick-add">
      <label className="subtext" htmlFor="template-quick-add">
        Add from a template
      </label>
      <div className="row gap">
        <Select
          id="template-quick-add"
          options={templateOptions(templates.data ?? [])}
          value={selected === '' ? null : selected}
          onChange={(next) => {
            setSelected(next ?? '');
            setAdded(null);
            setError(null);
          }}
          renderOption={renderTemplateOption}
          ariaLabel="Add from a template"
          placeholder="Choose a template…"
          emptyLabel="Choose a template…"
        />

        {variants.length === 0 && (
          <button
            type="button"
            disabled={selected === '' || add.isPending}
            onClick={() => add.mutate(undefined)}
          >
            Add
          </button>
        )}
      </div>

      {variants.length > 0 && (
        <div className="row gap template-variants">
          {variants.map((variant) => (
            <button
              key={variant.id}
              type="button"
              disabled={add.isPending}
              onClick={() => add.mutate(variant.name)}
            >
              {variant.name}
            </button>
          ))}
        </div>
      )}

      {added && <p className="subtext">Added “{added}” — drag it onto the day.</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
